import { watch, onMounted, onBeforeUnmount } from 'vue'
import { useState } from 'nuxt/app'

/**
 * SessionStorage 기반의 지속 가능한 전역 상태 제공 유틸리티
 * @param key 상태의 고유 키 (sessionStorage/전역 상태 공유용)
 * @param defaultValue 초기값 생성 함수
 * @returns Ref<T> 형태의 반응형 상태
 */
export function usePersistentState<T>(
    key: string,
    defaultValue: () => T
) {
    let initialValue: T;

    if (process.client) {
        try {
            const raw = sessionStorage.getItem(key)
            initialValue = raw ? (JSON.parse(raw) as T) : defaultValue()
        } catch (e) {
            console.warn(`Failed to parse sessionStorage for key "${key}"`, e)
            initialValue = defaultValue()
        }
    } else {
        initialValue = defaultValue()
    }

    const state = useState<T>(key, () => initialValue)

    if (process.client) {
        // 상태 변화 시 sessionStorage에 저장
        const stopWatch = watch(
            state,
            (newVal) => {
                try {
                    sessionStorage.setItem(key, JSON.stringify(newVal))
                } catch (e) {
                    console.warn(`Failed to save to sessionStorage for key "${key}"`, e)
                }
            },
            { deep: true }
        )

        // storage 이벤트로 다른 탭/clear() 감지하여 상태 초기화
        const onStorage = (e: StorageEvent) => {
            if (e.storageArea === sessionStorage && e.key === key && e.newValue === null) {
                state.value = defaultValue()
            }
        }

        onMounted(() => {
            window.addEventListener('storage', onStorage)
        })
        onBeforeUnmount(() => {
            stopWatch()
            window.removeEventListener('storage', onStorage)
        })
    }

    return state
}